import hr from './locales/hr';
import en from './locales/en';
import type { DeepString } from './types';

export const LOCALES = ['hr', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'hr';

/** hrvatski je na korijenu, engleski pod /en/ — vidi ARHITEKTURA.md sekcija 13. */
export function localePrefix(locale: Locale): string {
  return locale === DEFAULT_LOCALE ? '' : `/${locale}`;
}

export function isLocale(v: string): v is Locale {
  return (LOCALES as readonly string[]).includes(v);
}

/** Slugovi se prevode — SEO zahtjev. Ključ je kanonsko ime rute. */
export const ROUTE_SLUGS = {
  home: { hr: '', en: '' },
  about: { hr: 'o-treneru', en: 'about' },
  online: { hr: 'online-trening', en: 'online-training' },
  gym: { hr: 'trening-uzivo', en: 'gym-training' },
  pricing: { hr: 'cjenik', en: 'pricing' },
  results: { hr: 'transformacije', en: 'results' },
  faq: { hr: 'faq', en: 'faq' },
  contact: { hr: 'kontakt', en: 'contact' },
  app: { hr: 'aplikacija', en: 'app' },
  terms: { hr: 'uvjeti-koristenja', en: 'terms' },
  privacy: { hr: 'privatnost', en: 'privacy' },
} as const satisfies Record<string, Record<Locale, string>>;

export type RouteKey = keyof typeof ROUTE_SLUGS;

export function path(key: RouteKey, locale: Locale): string {
  const slug = ROUTE_SLUGS[key][locale];
  const prefix = localePrefix(locale);
  return slug ? `${prefix}/${slug}` : prefix || '/';
}

const dictionaries: Record<Locale, DeepString<typeof hr>> = { hr, en };
export type Dictionary = DeepString<typeof hr>;

export function dict(locale: Locale): Dictionary {
  return dictionaries[locale] as Dictionary;
}

/** t('nav.pricing') — plitki dot-path pristup, bez runtime ovisnosti. */
export function translator(locale: Locale) {
  const d = dict(locale);
  return function t(key: string, vars?: Record<string, string | number>): string {
    /*
      Kljuc koji nije niz srusio je cijeli ekran Profila: mapa vrsta obavijesti
      nije imala dvije nove vrste, `TYPE_KEY[row.type]` je bio `undefined`, a
      `undefined.split` je bijeli ekran. Jedan nedostajuci prijevod ne smije
      oboriti stranicu — u najgorem slucaju se vidi kljuc.
    */
    if (typeof key !== 'string' || key === '') return '';
    const parts = key.split('.');
    let cur: unknown = d;
    for (const p of parts) {
      if (typeof cur !== 'object' || cur === null || !(p in cur)) return key;
      cur = (cur as Record<string, unknown>)[p];
    }
    if (typeof cur !== 'string') return key;
    if (!vars) return cur;
    return cur.replace(/\{(\w+)\}/g, (_, v: string) => String(vars[v] ?? `{${v}}`));
  };
}

export { hr, en };
